import { api } from '@/lib/http-client';
import type { QueryParams } from '@/types/api';
import type {
  BorradoLeadResultado,
  ExportClientesDescarga,
  Kanban,
  KanbanFiltros,
  LeadCard,
  LeadDetalle,
  LeadFiltros,
  LeadsFiltros,
  ListaLeads,
  PurgaLeadsRequest,
  PurgaLeadsResultado,
  RecalculoLeadResultado,
  RecalculoMasivoRequest,
  RecalculoMasivoResultado,
  UpdateLeadStatusRequest,
} from '@/types/crm';

/**
 * Módulo 9 — CRM (kanban de leads, `docs/CRM.md` §6).
 *
 * Rutas reales (`backend/src/modules/crm/crm.routes.ts`), todas bajo `authenticate` +
 * `requireTenant()`: ver el kanban, ver un lead y reclasificarlo lo puede hacer cualquier miembro
 * del negocio (el AGENTE_HUMANO clasifica conversaciones); el recálculo MASIVO es solo ADMIN
 * (`requireRole('ADMIN')`). El aislamiento entre negocios lo garantiza el backend: un id de otra
 * empresa responde 404.
 *
 * Tres cosas del contrato que no se pueden adivinar:
 *  - el lead se identifica por la CONVERSACIÓN (`:conversationId`), porque `estado_lead` vive en
 *    `conversations`: el kanban, el detalle y las escrituras van por ese id;
 *  - el kanban NO pagina: devuelve las 4 columnas con hasta `limitePorColumna` tarjetas cada una
 *    (default 30, máx. 200) y el total REAL por columna, que puede ser mayor que lo que trajo;
 *  - el `PATCH .../estado` devuelve un `LeadCard` (SIN historial ni señales: eso es del detalle) y
 *    responde 409 si la conversación ya está en ese estado y además reclasificada a mano.
 */

/**
 * Filtros → query params, sin mandar los que no van.
 *
 * `botActivo` viaja como `'true'` / `'false'`: en la query el backend lo valida con
 * `z.enum(['true', 'false'])` (`crm.schemas.ts`), no con una coerción booleana (un `'false'` de
 * query string se leería como `true`). En el body del recálculo sí viaja como boolean.
 */
export function leadQueryFrom(filters: LeadFiltros = {}): QueryParams {
  const query: QueryParams = {};

  if (filters.estadoLead) query.estadoLead = filters.estadoLead;
  if (filters.estadoLeadOrigen) query.estadoLeadOrigen = filters.estadoLeadOrigen;
  if (filters.channel) query.channel = filters.channel;
  if (filters.adId) query.adId = filters.adId;
  if (filters.campaignId) query.campaignId = filters.campaignId;
  if (filters.botActivo !== undefined) query.botActivo = filters.botActivo ? 'true' : 'false';
  if (filters.asistenteActivo) query.asistenteActivo = filters.asistenteActivo;
  if (filters.buscar) query.buscar = filters.buscar;
  if (filters.desde) query.desde = filters.desde;
  if (filters.hasta) query.hasta = filters.hasta;

  return query;
}

/** Filtros del kanban: agrega `limitePorColumna` (1-200). */
export function kanbanQueryFrom(filters: KanbanFiltros = {}): QueryParams {
  const query = leadQueryFrom(filters);
  if (filters.limitePorColumna !== undefined) query.limitePorColumna = filters.limitePorColumna;
  return query;
}

/** Filtros del listado: agrega `limite` (1-200) y `offset` (0-100000). */
export function leadsQueryFrom(filters: LeadsFiltros = {}): QueryParams {
  const query = leadQueryFrom(filters);
  if (filters.limite !== undefined) query.limite = filters.limite;
  if (filters.offset !== undefined) query.offset = filters.offset;
  return query;
}

/**
 * `GET /api/tenants/:tenantId/crm/kanban` — el tablero por estados.
 *
 * Es la lectura principal del CRM: cada columna trae su total real y hasta `limitePorColumna`
 * tarjetas ordenadas por actividad reciente (las conversaciones sin mensajes van al final).
 */
export function fetchKanban(tenantId: string, filters: KanbanFiltros = {}): Promise<Kanban> {
  return api.get<Kanban>(`/api/tenants/${tenantId}/crm/kanban`, {
    query: kanbanQueryFrom(filters),
  });
}

/** `GET /api/tenants/:tenantId/crm/leads` — listado paginado + `totalesPorEstado`. */
export function listLeads(tenantId: string, filters: LeadsFiltros = {}): Promise<ListaLeads> {
  return api.get<ListaLeads>(`/api/tenants/${tenantId}/crm/leads`, {
    query: leadsQueryFrom(filters),
  });
}

/**
 * `GET /api/tenants/:tenantId/crm/leads/:conversationId` — la tarjeta + el POR QUÉ de su estado
 * (señales, clasificación automática, historial, órdenes y últimos 20 mensajes).
 */
export function fetchLead(tenantId: string, conversationId: string): Promise<LeadDetalle> {
  return api.get<LeadDetalle>(
    `/api/tenants/${tenantId}/crm/leads/${conversationId}`,
  );
}

/**
 * `PATCH .../crm/leads/:conversationId/estado` (cualquier miembro) — reclasificación MANUAL.
 *
 * Deja `estadoLeadOrigen = 'MANUAL'` y una fila en `lead_status_history` con el motivo (si no se
 * escribe nada, el backend guarda su motivo por defecto). Responde `LeadCard`.
 */
export function updateLeadStatus(
  tenantId: string,
  conversationId: string,
  payload: UpdateLeadStatusRequest,
): Promise<LeadCard> {
  return api.patch<LeadCard>(
    `/api/tenants/${tenantId}/crm/leads/${conversationId}/estado`,
    payload,
  );
}

/**
 * `POST .../crm/leads/:conversationId/recalcular` (cualquier miembro) — devuelve el lead a la
 * clasificación AUTOMÁTICA (deshace la reclasificación manual).
 *
 * `actualizado: false` significa que el análisis de hoy coincide con lo que ya estaba guardado.
 */
export function recalculateLead(
  tenantId: string,
  conversationId: string,
): Promise<RecalculoLeadResultado> {
  return api.post<RecalculoLeadResultado>(
    `/api/tenants/${tenantId}/crm/leads/${conversationId}/recalcular`,
  );
}

/**
 * `POST .../crm/recalcular` (solo ADMIN) — recálculo en lote, el que conviene correr una vez al
 * activar el CRM (clasifica las conversaciones que ya existían).
 *
 * Por defecto NO toca los leads reclasificados a mano (`incluirManuales: false`).
 */
export function recalculateLeads(
  tenantId: string,
  payload: RecalculoMasivoRequest = {},
): Promise<RecalculoMasivoResultado> {
  return api.post<RecalculoMasivoResultado>(`/api/tenants/${tenantId}/crm/recalcular`, payload);
}

/**
 * `GET .../crm/leads/export.csv` (solo ADMIN) — descarga la lista de clientes en CSV.
 *
 * Es la única llamada del módulo que NO recibe un JSON con envelope: el backend manda el archivo
 * (`text/csv`, con BOM y separador `;`) y los conteos en cabeceras `X-Export-*`. Por eso se pide con
 * `responseType: 'blob'`, que devuelve la `Response` cruda: de ahí salen el `Blob` para guardar y los
 * conteos. Los filtros son los MISMOS del listado (`limite`/`offset` sobran: la descarga no pagina).
 */
export async function exportLeadsCsv(
  tenantId: string,
  filters: LeadFiltros = {},
): Promise<ExportClientesDescarga> {
  const response = await api.get<Response>(`/api/tenants/${tenantId}/crm/leads/export.csv`, {
    query: leadQueryFrom(filters),
    responseType: 'blob',
  });

  const filas = Number(response.headers.get('X-Export-Filas'));
  const maxFilas = Number(response.headers.get('X-Export-Max-Filas'));

  return {
    blob: await response.blob(),
    filas: Number.isFinite(filas) ? filas : 0,
    truncado: response.headers.get('X-Export-Truncado') === 'true',
    maxFilas: Number.isFinite(maxFilas) ? maxFilas : 0,
  };
}

/**
 * `DELETE .../crm/leads/:conversationId` (solo ADMIN) — borra UNA conversación del negocio.
 *
 * Es el borrado del «bórrame mis datos» de un cliente. El backend responde 409 si la conversación
 * tiene órdenes (una venta no puede quedarse sin el chat que la originó) y 404 si es de otro negocio;
 * los dos mensajes se muestran tal cual.
 */
export function deleteLead(
  tenantId: string,
  conversationId: string,
): Promise<BorradoLeadResultado> {
  return api.delete<BorradoLeadResultado>(`/api/tenants/${tenantId}/crm/leads/${conversationId}`);
}

/**
 * `POST .../crm/purgar` (solo ADMIN) — purga por lotes de conversaciones inactivas.
 *
 * Sin `confirmar: true` es una SIMULACIÓN (`eliminadas: 0`): devuelve cuántas se borrarían
 * (`seleccionadas`), cuántas quedan protegidas por tener órdenes y hasta 10 ejemplos, para que el
 * admin revise el alcance antes de confirmar.
 */
export function purgeLeads(
  tenantId: string,
  payload: PurgaLeadsRequest = {},
): Promise<PurgaLeadsResultado> {
  return api.post<PurgaLeadsResultado>(`/api/tenants/${tenantId}/crm/purgar`, payload);
}
